<template>
  <Dialog :open="open" @update:open="$emit('update:open', $event)">
    <DialogContent class="max-w-[450px] rounded-xl p-0" @pointerDownOutside.prevent @escapeKeyDown.prevent preventClose>
      <div v-if="isLoading" class="flex items-center justify-center p-12">
        <Loader2 class="h-8 w-8 animate-spin text-brand-600" />
      </div>

      <WaitingForMerchant
        v-else-if="view === states.WAITING"
        :is-submitting="isSubmitting"
        @withdraw="view = states.CONFIRM_WITHDRAW"
        @close="close"
      />

      <MerchantResponse
        v-else-if="view === states.RESPOND"
        :order="order"
        :last-notification="lastNotification"
        :cancellation-fee="cancellationFee"
        :is-submitting="isSubmitting"
        @accept="view = states.CONFIRM_ACCEPT"
        @dispute="showDisputeDialog = true"
        @reject="rejectTerms"
      />

      <ConfirmAction
        v-else-if="view === states.CONFIRM_ACCEPT"
        title="Are you sure you want to cancel this order?"
        :description="`By selecting 'YES', ${suspendText} ${feeText}`"
        :is-submitting="isSubmitting"
        @confirm="acceptCancellation"
        @cancel="view = states.RESPOND"
      />

      <ConfirmAction
        v-else-if="view === states.CONFIRM_WITHDRAW"
        title="Withdraw Cancellation Request"
        description="Are you sure you want to withdraw and remove this cancellation request?"
        :is-submitting="isSubmitting"
        @confirm="withdrawCancellation"
        @cancel="view = states.WAITING"
      />

      <CancellationPolicy
        v-else-if="view === states.STEP_1"
        v-model:selected-reason="selectedReason"
        v-model:agreed-to-policy="agreedToPolicy"
        :refund-before-deductions="refundBeforeDeductions"
        :cancellation-fee="cancellationFee"
        :refund-after-deductions="refundAfterDeductions"
        :cancellation-reasons="cancellationReasons"
        @continue="view = states.STEP_2"
        @dispute="showDisputeDialog = true"
        @close="close"
      />

      <CancellationReason
        v-else-if="view === states.STEP_2"
        v-model:selected-reason="selectedReason"
        v-model:cancellation-message="cancellationMessage"
        :refund-before-deductions="refundBeforeDeductions"
        :cancellation-fee="cancellationFee"
        :refund-after-deductions="refundAfterDeductions"
        :merchant-name="order?.merchantName ?? ''"
        :cancellation-reasons="cancellationReasons"
        @next="view = states.STEP_3"
        @close="close"
      />

      <CancellationReview
        v-else-if="view === states.STEP_3"
        :order="order"
        :selected-reason="selectedReason"
        :refund-before-deductions="refundBeforeDeductions"
        :cancellation-fee="cancellationFee"
        :refund-after-deductions="refundAfterDeductions"
        :deposit-message="depositMessage"
        @next="view = states.STEP_4"
        @close="close"
      />

      <ConfirmAction
        v-else-if="view === states.STEP_4"
        title="Cancel this order?"
        description="By selecting 'YES' you acknowledge that this action cannot be reversed."
        :is-submitting="isSubmitting"
        @confirm="acceptCancellation"
        @cancel="close"
      />
    </DialogContent>
  </Dialog>

  <DisputeCancellationDialog
    :open="showDisputeDialog"
    :order="order"
    :cancellation-reason="selectedReason || lastNotification?.reason || ''"
    @update:open="showDisputeDialog = $event"
    @disputed="onDisputeComplete"
  />

  <SuccessErrorModal
    :show-modal="showErrorModal"
    title="Error"
    description="Failed to load cancellation details. Please try again."
    :is-error="true"
    @close="showErrorModal = false"
  />
</template>

<script setup lang="ts">
import SuccessErrorModal from '@/components/common/SuccessErrorModal.vue';
import CancellationPolicy from '@/components/shopper/lay-by/cancellation-request/CancellationPolicy.vue';
import CancellationReason from '@/components/shopper/lay-by/cancellation-request/CancellationReason.vue';
import CancellationReview from '@/components/shopper/lay-by/cancellation-request/CancellationReview.vue';
import ConfirmAction from '@/components/shopper/lay-by/cancellation-request/ConfirmAction.vue';
import DisputeCancellationDialog from '@/components/shopper/lay-by/cancellation-request/DisputeCancellationDialog.vue';
import MerchantResponse from '@/components/shopper/lay-by/cancellation-request/MerchantResponse.vue';
import WaitingForMerchant from '@/components/shopper/lay-by/cancellation-request/WaitingForMerchant.vue';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { NetworkError } from '@/errors';
import { getOrderCancellationPreviewFee, updateOrder } from '@/services/orders';
import { useUserStore } from '@/stores/users';
import dayjs from '@/utils/dayjs.utils';
import { displayCurrency } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { Loader2 } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';

const props = defineProps<{
  open: boolean;
  order?: ModelOrderResponse;
}>();

const emit = defineEmits<{
  'update:open': [value: boolean];
  cancelled: [];
}>();

const { user } = useUserStore();
const states = {
  WAITING: 'waiting',
  RESPOND: 'respond',
  CONFIRM_ACCEPT: 'confirm-accept',
  CONFIRM_WITHDRAW: 'confirm-withdraw',
  STEP_1: 'step-1',
  STEP_2: 'step-2',
  STEP_3: 'step-3',
  STEP_4: 'step-4',
} as const;

type View = (typeof states)[keyof typeof states];

const view = ref<View>(states.STEP_1);
const selectedReason = ref('');
const cancellationMessage = ref('');
const agreedToPolicy = ref(false);
const isLoading = ref(false);
const isSubmitting = ref(false);
const cancellationFee = ref(0);
const showDisputeDialog = ref(false);
const showErrorModal = ref(false);

const cancellationReasons = [
  'Bought in error (expected to get the goods immediately)',
  'Bought the wrong item',
  'I can no longer afford to pay my plan',
  'Product or service not available',
  'Other',
];

const lastNotification = computed(() => {
  const notes = props.order?.notifications;
  return notes?.length ? notes[notes.length - 1] : undefined;
});

const refundBeforeDeductions = computed(() => {
  const total = props.order?.amountDue ?? 0;
  const balance = props.order?.balance ?? 0;
  return total - balance;
});

const refundAfterDeductions = computed(() => Math.max(0, refundBeforeDeductions.value - cancellationFee.value));

const depositMessage = computed(() => {
  if (!user?.name || cancellationFee.value <= 0) return '';
  const firstName = user.name.split(' ')[0];
  return `Hi there ${firstName}, please note that your cancellation request has been received and your deposit will be taken as a cancellation fee.`;
});

const suspendText = computed(() =>
  props.order?.suspendOnCancel
    ? 'your order will be suspended and the merchant will be notified to discuss the cancellation.'
    : 'you acknowledge that the request cannot be reversed.',
);

const feeText = computed(() =>
  cancellationFee.value > 0 ? `You will be charged a fee of ${displayCurrency(cancellationFee.value)}.` : '',
);

const close = () => emit('update:open', false);

const determineInitialView = (): View => {
  const order = props.order;
  if (!order) return states.STEP_1;

  const request = order.orderCancellationRequest;
  const notes = order.notifications;
  const lastNote = notes?.length ? notes[notes.length - 1] : undefined;

  if (!request || request === 'WITHDRAWN' || request === 'REJECTED') return states.STEP_1;

  if (request === 'PENDING' || request === 'DISPUTE') {
    if (lastNote?.userId === user?._id && lastNote?.change !== 'REJECTED') return states.WAITING;
    return states.RESPOND;
  }

  return states.STEP_1;
};

const markLastNotificationRead = (notifications: any[]) => {
  const lastNote = notifications.at(-1);
  if (lastNote && lastNote.userId !== user?._id) {
    lastNote.read = true;
  }
};

const pushNotification = (notifications: any[], change: string, details: string) => {
  notifications.push({
    change,
    date: dayjs().format('Do MMM YYYY, h:mma'),
    name: user?.name ?? '',
    reason: selectedReason.value || lastNotification.value?.reason || '',
    details,
    read: false,
    userId: user?._id,
    type: 'CANCELLATION',
  });
};

const handleAction = async (
  action: string,
  updateData: Record<string, any>,
  detailsMessage: string,
  markOwnRead = false,
) => {
  if (!props.order?._id) return;
  isSubmitting.value = true;
  try {
    const notifications = [...(props.order.notifications ?? [])];
    if (markOwnRead) {
      const lastNote = notifications.at(-1);
      if (lastNote && lastNote.userId === user?._id) lastNote.read = true;
    } else {
      markLastNotificationRead(notifications);
    }
    pushNotification(notifications, updateData.change ?? action.toUpperCase(), detailsMessage);

    const { change: _, ...orderUpdate } = updateData;
    await updateOrder(props.order._id, { ...orderUpdate, notifications });

    addMetric('cancellation_request', { status: 'success', channel: 'shopper', action });
    emit('cancelled');
    close();
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('cancellation_request', { status: 'network_error', channel: 'shopper', action });
    } else {
      createException(error);
      addMetric('cancellation_request', { status: 'error', channel: 'shopper', action });
    }
  } finally {
    isSubmitting.value = false;
  }
};

const acceptCancellation = () =>
  handleAction(
    'accept',
    {
      change: 'ACCEPTED',
      orderCancellationRequest: 'ACCEPTED',
      acceptedProposedCancellationTerms: true,
      state: 'CANCELLING',
    },
    'The customer has accepted the cancellation terms for this order.',
  );

const rejectTerms = () => {
  const request = props.order?.orderCancellationRequest;
  if (request !== 'PENDING' && request !== 'DISPUTE') {
    close();
    return;
  }
  handleAction(
    'reject',
    {
      change: 'REJECTED',
      orderCancellationRequest: 'REJECTED',
      acceptedProposedCancellationTerms: false,
    },
    'The customer has rejected the terms and will continue with the order',
  );
};

const withdrawCancellation = () => {
  const request = props.order?.orderCancellationRequest;
  if (request !== 'PENDING' && request !== 'DISPUTE') {
    close();
    return;
  }
  handleAction(
    'withdraw',
    {
      change: 'WITHDRAWN',
      orderCancellationRequest: 'WITHDRAWN',
      acceptedProposedCancellationTerms: false,
    },
    'The customer has withdrawn the cancellation request for this order.',
    true,
  );
};

const onDisputeComplete = () => {
  showDisputeDialog.value = false;
  emit('cancelled');
  close();
};

watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      selectedReason.value = '';
      cancellationMessage.value = '';
      agreedToPolicy.value = false;
      isSubmitting.value = false;
      showDisputeDialog.value = false;
      isLoading.value = true;

      try {
        if (props.order?._id) {
          cancellationFee.value = await getOrderCancellationPreviewFee(props.order._id);
        }
      } catch (error) {
        createException(error);
        showErrorModal.value = true;
        cancellationFee.value = 0;
      } finally {
        isLoading.value = false;
      }

      if (props.order?.notifications?.length) {
        const lastNote = props.order.notifications.at(-1);
        if (lastNote?.reason) selectedReason.value = lastNote.reason;
      }

      view.value = determineInitialView();
    }
  },
);
</script>
